import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API, Badge, Frist, de, dt, fehler, feld, heute, oeffnen } from "./shared";

const LEER = { eingang_am: heute(), von_art: "Teilnehmende", von_name: "", kanal: "Telefon", massnahme: "", betreff: "", beschreibung: "", verantwortlich: "", frist: "" };
const TON = { eingegangen: "gelb", in_bearbeitung: "gelb", abgeschlossen: "gruen" };

const Neu = ({ onFertig }) => {
  const [b, setB] = useState(LEER);
  const s = (k) => (e) => setB({ ...b, [k]: e.target.value });
  const senden = (e) => { e.preventDefault(); axios.post(`${API}/beschwerden`, b).then(() => { toast.success("Beschwerde erfasst"); setB(LEER); onFertig(); }).catch(fehler); };
  return (
    <form onSubmit={senden} className="grid grid-cols-2 md:grid-cols-4 gap-2 bg-white rounded-xl p-4 border border-[#F0E6DC]" data-testid="beschwerde-form">
      <input className={feld} type="date" required value={b.eingang_am} onChange={s("eingang_am")} data-testid="beschwerde-eingang" />
      <select className={feld} value={b.von_art} onChange={s("von_art")} data-testid="beschwerde-von-art">{["Teilnehmende", "Pflegeeinrichtung", "Agentur/Jobcenter", "Dozent/in", "Sonstige"].map((x) => <option key={x}>{x}</option>)}</select>
      <input className={feld} placeholder="Name (optional)" value={b.von_name} onChange={s("von_name")} data-testid="beschwerde-von-name" />
      <select className={feld} value={b.kanal} onChange={s("kanal")} data-testid="beschwerde-kanal">{["Telefon", "E-Mail", "persönlich", "Brief", "WhatsApp"].map((x) => <option key={x}>{x}</option>)}</select>
      <input className={`${feld} col-span-2`} placeholder="Betreff *" required value={b.betreff} onChange={s("betreff")} data-testid="beschwerde-betreff" />
      <input className={feld} placeholder="Maßnahme / Kurs" value={b.massnahme} onChange={s("massnahme")} data-testid="beschwerde-massnahme" />
      <input className={feld} placeholder="Verantwortlich *" required value={b.verantwortlich} onChange={s("verantwortlich")} data-testid="beschwerde-verantwortlich" />
      <textarea className={`${feld} col-span-2 md:col-span-3`} rows={2} placeholder="Was genau ist passiert? *" required value={b.beschreibung} onChange={s("beschreibung")} data-testid="beschwerde-beschreibung" />
      <div className="flex gap-2"><input className={feld} type="date" required value={b.frist} onChange={s("frist")} title="Frist" data-testid="beschwerde-frist" />
        <Button type="submit" size="sm" className="bg-[#9A3B2E] hover:bg-[#7F2F25]" data-testid="beschwerde-anlegen">Erfassen</Button></div>
    </form>
  );
};

const Eintrag = ({ b, laden }) => {
  const [offen, setOffen] = useState(false);
  const [text, setText] = useState("");
  const notiz = () => axios.post(`${API}/beschwerden/${b.id}/verlauf`, { text }).then(() => { setText(""); laden(); }).catch(fehler);
  const abschluss = () => {
    const ergebnis = window.prompt("Ergebnis / Lösung für die beschwerdeführende Person:");
    if (!ergebnis) return;
    const korrekturmassnahme = window.prompt("Korrekturmaßnahme, damit es nicht wieder passiert (optional):") || "";
    axios.put(`${API}/beschwerden/${b.id}/abschliessen`, { ergebnis, korrekturmassnahme }).then(() => { toast.success("Abgeschlossen"); laden(); }).catch(fehler);
  };
  return (
    <div className="p-3 text-sm" data-testid={`beschwerde-${b.nummer}`}>
      <button className="w-full flex flex-wrap items-center gap-x-4 gap-y-1 text-left" onClick={() => setOffen(!offen)}>
        <span className="font-mono text-xs">{b.nummer}</span><span className="font-medium">{b.betreff}</span>
        <span className="text-[#78716c]">{b.von_art}{b.von_name && ` – ${b.von_name}`} · {b.kanal} · {de(b.eingang_am)}</span>
        <span className="text-[#78716c]">{b.verantwortlich}</span><Frist frist={b.frist} erledigt={b.status === "abgeschlossen"} />
        <span className="ml-auto"><Badge ton={TON[b.status]} testid={`beschwerde-status-${b.nummer}`}>{b.status.replace("_", " ")}</Badge></span>
      </button>
      {offen && (
        <div className="mt-2 ml-2 border-l-2 border-[#F0E6DC] pl-3 space-y-1">
          <p className="text-[#5C534D]">{b.beschreibung}</p>
          {b.verlauf.map((v, i) => <p key={i} className="text-xs text-[#78716c]">{dt(v.am)} · {v.von}: {v.text}</p>)}
          {b.status === "abgeschlossen" ? <p className="text-xs text-[#4F6650]">Korrekturmaßnahme: {b.korrekturmassnahme || "–"}</p> : (
            <div className="flex gap-2 pt-1">
              <input className={feld} placeholder="Bearbeitungsschritt notieren" value={text} onChange={(e) => setText(e.target.value)} data-testid={`beschwerde-notiz-${b.nummer}`} />
              <Button size="sm" variant="outline" disabled={!text.trim()} onClick={notiz} data-testid={`beschwerde-notiz-speichern-${b.nummer}`}>Notieren</Button>
              <Button size="sm" onClick={abschluss} data-testid={`beschwerde-abschliessen-${b.nummer}`}>Abschließen</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const QMBeschwerden = () => {
  const [liste, setListe] = useState([]);
  const laden = useCallback(() => axios.get(`${API}/beschwerden`).then((r) => setListe(r.data)).catch(fehler), []);
  useEffect(() => { laden(); }, [laden]);
  return (
    <div className="space-y-4" data-testid="qm-beschwerden">
      <Neu onFertig={laden} />
      <div className="flex justify-end"><Button size="sm" variant="outline" onClick={() => oeffnen("/beschwerden/pdf")} data-testid="beschwerden-pdf"><FileText size={14} className="mr-1" />Übersicht als PDF</Button></div>
      <div className="bg-white rounded-xl border border-[#F0E6DC] divide-y divide-[#F6E3DA]">
        {liste.length === 0 && <p className="p-4 text-sm text-[#8E7B71]">Noch keine Beschwerden erfasst.</p>}
        {liste.map((b) => <Eintrag key={b.id} b={b} laden={laden} />)}
      </div>
    </div>
  );
};
